import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { HiPlus } from 'react-icons/hi2';
import {
  KidStatus,
  KidTemplateAffectedShareClass,
} from '../../../../../api/kids/types';
import type {
  KidDocumentForm,
  PdfElement,
} from '../../../raidr/kidsV2/types/KidPdfGeneratorV2.types';
import {
  useAddTemplateToDocument,
  useGenerateKidDocument,
} from '../../../../../api/kids/hooks/mutations';
import {
  useGetKidData,
  useGetKidMarkerAll,
  useGetKidTemplate,
} from '../../../../../api/kids/hooks/queries';
import { useGetClient } from '../../../../../api/client';
import { toast } from '../../../../toast/Toast';
import {
  buildTemplatePdfElements,
} from '../../../raidr/kidsV2/components/form/templateSelection.utils';
import { transformKidDataModelsToElements } from '../../../raidr/kidsV2/utils/kidDataTransformers';
import DocumentPdfPreview from '../../../raidr/kidsV2/components/form/DocumentPdfPreview';
import GenericModal from '../../../../ui/GenericModal';
import { btnPrimary, btnSecondary } from '../templateManagement.constants';
import {
  formatKidTemplateShareClassName,
} from '../templateDocumentRows.utils';
import { toSelectableKidTemplate, buildKidEditorV2Url } from '../templateManagement.utils';
import { AddTemplateToDocumentContext } from '../templateManagement.types';
import TemplateDocumentMeta from './TemplateDocumentMeta';

const describeBlockType = (element: PdfElement): string => {
  if (element.template_id) return 'Template';
  return element.tag
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const stripHtml = (value: string): string => {
  const withoutTags = value.replace(/<[^>]*>/g, ' ');
  const decoded = withoutTags
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)));
  return decoded
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const snippetFromContent = (content: PdfElement['content']): string => {
  if (content == null) return '';
  if (typeof content === 'number') return String(content);
  if (typeof content === 'string') return stripHtml(content);
  if (!Array.isArray(content) || content.length === 0) return '';

  if (typeof content[0] === 'string') {
    return (content as string[]).map(stripHtml).filter(Boolean).join(' · ');
  }

  if (Array.isArray(content[0])) {
    const firstRow = (content as string[][])[0] ?? [];
    return firstRow
      .map((cell) => stripHtml(String(cell ?? '')))
      .filter(Boolean)
      .join(' · ');
  }

  const nested = content[0] as { content?: PdfElement['content'] };
  if (nested && typeof nested === 'object' && 'content' in nested) {
    return snippetFromContent(nested.content);
  }

  return '';
};

const getBlockSnippet = (element: PdfElement): string => {
  if (element.template_id) {
    const templateName = element.template?.template_name?.trim();
    if (templateName) return templateName;
  }
  if (element.title?.trim()) return stripHtml(element.title);
  if (typeof element.rendered_content === 'string') {
    const rendered = stripHtml(element.rendered_content);
    if (rendered) return rendered;
  }
  return snippetFromContent(element.content);
};

const formatBlockSnippet = (
  element: PdfElement,
  resolveTokens: (value: unknown) => unknown,
): string => {
  const raw = getBlockSnippet(element);
  const resolved = resolveTokens(raw);
  return typeof resolved === 'string' ? stripHtml(resolved) : raw;
};

const AddTemplateToDocumentModal: FC<
  AddTemplateToDocumentContext & {
    isOpen: boolean;
    onClose: () => void;
    document: KidTemplateAffectedShareClass;
  }
> = ({ isOpen, onClose, document, clientId, template }) => {
  const [insertIndex, setInsertIndex] = useState<number | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const addTemplateToDocument = useAddTemplateToDocument(clientId);
  const generatePdf = useGenerateKidDocument();

  const { data: clientData } = useGetClient(clientId);
  const {
    data: kidData,
    isLoading,
    isError,
  } = useGetKidData(
    clientId,
    document.fund_id,
    document.share_class_id,
    document.document_language,
  );
  const { data: markers } = useGetKidMarkerAll(
    clientId,
    document.fund_id,
    document.share_class_id,
    document.kid_figure_calculation_date,
    document.last_updating_datetime,
  );

  const {
    data: fetchedTemplate,
    isLoading: isTemplateLoading,
    isError: isTemplateError,
  } = useGetKidTemplate(
    clientId,
    template.template_id,
    {
      fundId: document.fund_id,
      shareClassId: document.share_class_id,
      kidFigureCalculationDate: document.kid_figure_calculation_date,
    },
    { enabled: isOpen },
  );

  const resolvedTemplate = useMemo(
    () =>
      fetchedTemplate ? toSelectableKidTemplate(fetchedTemplate) : template,
    [fetchedTemplate, template],
  );

  const baseElements = useMemo<PdfElement[]>(
    () => (kidData ? transformKidDataModelsToElements(kidData as any[]) : []),
    [kidData],
  );


  const existingTemplateIds = useMemo(
    () =>
      baseElements
        .map((element) => element.template_id)
        .filter((id): id is string => Boolean(id)),
    [baseElements],
  );

  const builtTemplateElements = useMemo(
    () =>
      isTemplateLoading || isTemplateError
        ? []
        : buildTemplatePdfElements([resolvedTemplate], existingTemplateIds),
    [
      resolvedTemplate,
      existingTemplateIds,
      isTemplateLoading,
      isTemplateError,
    ],
  );
  const alreadyInDocument =
    !isTemplateLoading &&
    !isTemplateError &&
    builtTemplateElements.length === 0;

  useEffect(() => {
    if (!isOpen) return;
    setInsertIndex(null);
  }, [isOpen, document.kid_id]);

  const previewElements = useMemo<PdfElement[]>(() => {
    if (!builtTemplateElements.length || insertIndex === null) return baseElements;
    const next = [...baseElements];
    next.splice(insertIndex, 0, ...builtTemplateElements);
    return next;
  }, [baseElements, builtTemplateElements, insertIndex]);

  const markerLookup = useMemo(() => {
    const lookup = new Map<string, string>();
    (markers || []).forEach((marker) => {
      lookup.set(`${marker.marker_type}:${marker.placeholder}`, marker.value);
    });
    return lookup;
  }, [markers]);

  const resolveMarkerTokens = useCallback(
    (value: unknown): unknown => {
      if (typeof value === 'string') {
        return value.replace(
          /\[\[([A-Z_]+):([a-z0-9_]+)\]\]/g,
          (match, markerType, placeholder) =>
            markerLookup.get(`${markerType}:${placeholder}`) || match,
        );
      }
      if (Array.isArray(value)) {
        return value.map((item) => resolveMarkerTokens(item));
      }
      return value;
    },
    [markerLookup],
  );

  const isDocumentLocked = document.status === KidStatus.EDITED;

  useEffect(() => {
    if (!isOpen || isLoading || isError || isTemplateLoading || isTemplateError) {
      return;
    }

    let cancelled = false;
    const run = async () => {
      try {
        const payload: KidDocumentForm = {
          client: clientData?.client_name_full || clientData?.client_name || '',
          fund_name: document.fund_name_long || document.fund_id,
          fund_name_full: document.fund_name_long || document.fund_id,
          share_class: formatKidTemplateShareClassName(document),
          date:
            document.kid_figure_calculation_date ||
            new Date().toISOString().split('T')[0],
          elements: previewElements.map((element) => ({
            ...element,
            content: resolveMarkerTokens(element.content) as PdfElement['content'],
          })),
        };

        const response = await generatePdf.mutateAsync(payload);
        if (cancelled) return;

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const fileUrl = window.URL.createObjectURL(blob);

        setPdfUrl((previous) => {
          if (previous) window.URL.revokeObjectURL(previous);
          return fileUrl;
        });
      } catch (error) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error('Failed to generate template preview PDF:', error);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    isLoading,
    isError,
    isTemplateLoading,
    isTemplateError,
    previewElements,
    clientData,
    resolveMarkerTokens,
  ]);

  useEffect(
    () => () => {
      setPdfUrl((previous) => {
        if (previous) window.URL.revokeObjectURL(previous);
        return null;
      });
    },
    [],
  );

  const handleAdd = () => {
    if (isDocumentLocked || insertIndex === null) return;

    const [block] = builtTemplateElements;
    if (!block) {
      toast({
        title: 'Already in document',
        message: `${template.template_name} is already in this document layout.`,
        type: 'default',
      });
      return;
    }

    addTemplateToDocument.mutate(
      {
        fund_id: document.fund_id,
        share_class_id: document.share_class_id,
        template_id: template.template_id,
        document_language: document.document_language,
        document_index: insertIndex,
        kid_tag: block.tag,
        format_options:
          (block.format_options as Record<string, unknown> | undefined) ?? undefined,
        kid_doc_layout:
          (block.layout as Record<string, unknown> | undefined) ?? undefined,
      },
      { onSuccess: () => onClose() },
    );
  };

  const isSaving = addTemplateToDocument.isPending;
  const isGenerating = generatePdf.isPending;

  const renderInsertSlot = (slotIndex: number) => {
    const isSelected = insertIndex === slotIndex;
    if (isSelected) {
      const rawContent =
        resolvedTemplate.rendered_content || resolvedTemplate.template_content || '';
      const snippet = stripHtml(String(resolveMarkerTokens(rawContent) ?? ''));
      return (
        <div className="rounded-md border border-dashed border-gray-400 bg-white px-2.5 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-mainblue">
            New template
          </p>
          <p
            className="mt-0.5 truncate text-xs font-medium text-gray-900"
            title={resolvedTemplate.template_name}
          >
            {resolvedTemplate.template_name}
          </p>
          {isTemplateLoading ? (
            <p className="mt-1 text-[11px] text-gray-500">Loading content…</p>
          ) : snippet ? (
            <p className="mt-1 line-clamp-3 text-[11px] leading-snug text-gray-600">
              {snippet}
            </p>
          ) : null}
        </div>
      );
    }
    return (
      <button
        type="button"
        onClick={() => setInsertIndex(slotIndex)}
        disabled={isDocumentLocked || alreadyInDocument}
        className="group flex w-full items-center gap-2 py-0.5 text-[11px] text-gray-400 transition-colors hover:text-mainblue disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className="h-px flex-1 bg-gray-200 transition-colors group-hover:bg-mainblue/40" />
        <span className="flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-2 py-0.5 font-semibold transition-colors group-hover:border-mainblue/50 group-hover:bg-mainblue/5">
          <HiPlus className="h-3 w-3" />
          Insert here
        </span>
        <span className="h-px flex-1 bg-gray-200 transition-colors group-hover:bg-mainblue/40" />
      </button>
    );
  };

  const renderPreview = () => (
    <DocumentPdfPreview
      previewUrl={pdfUrl}
      isPending={isLoading || isTemplateLoading || isGenerating}
      isError={isError || isTemplateError}
      title={null}
      className="h-full border-0 rounded-none"
      loadingMessage={
        isLoading || isTemplateLoading ? 'Loading document…' : 'Generating preview…'
      }
      errorMessage={
        isTemplateError
          ? 'Could not load the template details.'
          : 'Could not load the document layout.'
      }
    />
  );

  return (
    <GenericModal
      isOpen={isOpen}
      setIsOpen={(open) => {
        if (!open) onClose();
      }}
      title="Add template to document"
      className="max-w-6xl"
    >
      <div className="flex h-[72vh] min-h-0 flex-col">
        <TemplateDocumentMeta
          templateName={template.template_name}
          document={document}
        />

        {isDocumentLocked ? (
          <div className="mb-3 flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
            <p className="min-w-0 flex-1 text-sm text-amber-950">
              This document is marked ready for review. Mark it as draft in the
              editor before changing the layout.
            </p>
            <Link
              to={buildKidEditorV2Url(document)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-amber-950 shadow-sm transition-colors hover:border-amber-400 hover:bg-amber-100"
              title="Open in editor"
            >
              <FaExternalLinkAlt className="h-2.5 w-2.5" aria-hidden />
              Go to Editor
            </Link>
          </div>
        ) : null}

        <div className="flex min-h-0 flex-1 gap-4">
          <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200 bg-gray-100">
            {renderPreview()}
          </div>

          <div className="flex w-72 shrink-0 flex-col rounded-lg border border-gray-200 bg-white">
            <div className="shrink-0 border-b border-gray-100 px-3 py-2.5">
              <h3 className="text-xs font-bold uppercase tracking-wide text-gray-600">
                Layout
              </h3>
              <p className="mt-0.5 text-[11px] text-gray-500">
                {alreadyInDocument
                  ? 'This template is already in the document.'
                  : insertIndex === null
                    ? 'Select a position to insert the template.'
                    : 'Pick the position to insert the template.'}
              </p>
            </div>
            <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-3">
              {renderInsertSlot(0)}
              {baseElements.map((element, index) => {
                const snippet = formatBlockSnippet(element, resolveMarkerTokens);
                return (
                  <Fragment key={`${element.fieldId}-${index}`}>
                    <div
                      className="rounded-md border border-gray-200 bg-gray-50/80 px-2.5 py-1.5"
                      style={
                        element.template_id
                          ? { borderLeftWidth: 3, borderLeftColor: '#9ca3af' }
                          : undefined
                      }
                    >
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 w-4 shrink-0 text-[10px] font-bold tabular-nums text-gray-400">
                          {index + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                            {describeBlockType(element)}
                          </p>
                          {snippet ? (
                            <p
                              className="mt-0.5 line-clamp-2 text-xs font-medium leading-snug text-gray-800"
                              title={snippet}
                            >
                              {snippet}
                            </p>
                          ) : (
                            <p className="mt-0.5 text-xs italic text-gray-400">
                              No content
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    {renderInsertSlot(index + 1)}
                  </Fragment>
                );
              })}
              {baseElements.length === 0 ? (
                <p className="px-1 py-2 text-[11px] text-gray-500">
                  This document has no blocks yet. Choose where to insert the template.
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <div className="mt-4 flex shrink-0 justify-end gap-2 border-t border-gray-100 pt-4">
          <button
            type="button"
            className={btnSecondary}
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            className={btnPrimary}
            onClick={handleAdd}
            disabled={
              isSaving ||
              isLoading ||
              isTemplateLoading ||
              isError ||
              isTemplateError ||
              isDocumentLocked ||
              alreadyInDocument ||
              insertIndex === null ||
              !document.kid_id
            }
          >
            {isSaving ? 'Adding…' : 'Add to document'}
          </button>
        </div>
      </div>
    </GenericModal>
  );
};

export default AddTemplateToDocumentModal;
